import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { useStore } from '../../contexts/StoreProvider';

const PaymentCallbackScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchOrders, orders } = useStore();
  
  const status = searchParams.get('status');
  const transactionId = searchParams.get('transactionId');
  const clientRefRaw = searchParams.get('order_number') || searchParams.get('clientReference');
  const orderNumber = clientRefRaw ? clientRefRaw.split('-retry-')[0] : null;

  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // When returning to this page, the backend webhook should have updated the order status.
    // Let's refetch the user's orders so the StoreProvider is up to date.
    fetchOrders().finally(() => {
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSuccessParams = status === 'Success' || status === 'Completed' || status?.toLowerCase() === 'success' || status?.toLowerCase() === 'paid';
  const currentOrder = orders?.find(o => o.order_number === orderNumber);
  const isOrderPaid = currentOrder?.payment_status === 'PAID';
  
  const isSuccess = isSuccessParams || isOrderPaid;

  useEffect(() => {
    if (!isLoading && isSuccess) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        navigate('/store/orders');
      }
    }
  }, [isLoading, isSuccess, countdown, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 font-semibold uppercase tracking-wider">Verifying payment...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
      {isSuccess ? (
        <CheckCircle size={80} className="text-green-500 mb-6" />
      ) : (
        <XCircle size={80} className="text-red-500 mb-6" />
      )}
      
      <h1 className="text-3xl font-bold uppercase tracking-widest mb-4">
        {isSuccess ? 'Payment Successful' : 'Payment Failed'}
      </h1>
      
      <p className="text-gray-600 mb-8 max-w-md">
        {isSuccess 
          ? `Thank you for your purchase. Your order ${orderNumber ? `(${orderNumber})` : ''} has been confirmed and is being processed.`
          : 'Unfortunately, your payment could not be completed. Please check your payment details and try again.'}
      </p>

      {transactionId && (
        <p className="text-sm text-gray-400 mb-10">Transaction ID: {transactionId}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <Link 
          to="/store/orders"
          className="flex-1 bg-black text-white px-6 py-4 font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors text-center"
        >
          {isSuccess ? `View Orders (${countdown}s)` : 'View Orders'}
        </Link>
        {!isSuccess && (
          <Link 
            to="/store"
            className="flex-1 bg-white text-black border-2 border-black px-6 py-4 font-bold tracking-widest uppercase hover:bg-gray-50 transition-colors text-center"
          >
            Return to Store
          </Link>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackScreen;
